/**
 * Live Chat Management API Service
 * 
 * Handles API calls for real-time conversation monitoring,
 * agent management, and chat analytics.
 */

import { api } from './api'
import type {
  LiveConversation,
  Agent,
  ConversationHandoff,
  DashboardMetrics,
  LiveStatusSummary,
  ConversationFilters
} from '../types/liveChat'
import { logger } from '../utils/logger'

/**
 * Live Chat API Service Class
 */
class LiveChatService {
  
  // ====================================
  // CONVERSATION MANAGEMENT
  // ====================================
  
  /**
   * Get live conversations with filtering and pagination
   */
  async getLiveConversations(
    filters: ConversationFilters = {},
    limit: number = 50,
    offset: number = 0
  ): Promise<LiveConversation[]> {
    try {
      logger.debug('Fetching live conversations', {
        service: 'LiveChatService',
        action: 'get_live_conversations',
        filters,
        limit,
        offset
      })

      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      })

      // Add filters to params
      if (filters.status) params.append('status', filters.status)
      if (filters.chatbot_id) params.append('chatbot_id', filters.chatbot_id)
      if (filters.agent_id) params.append('agent_id', filters.agent_id)

      const response = await api.get(`/live-chat/conversations?${params}`)
      
      logger.info('Live conversations fetched successfully', {
        service: 'LiveChatService',
        action: 'conversations_fetched',
        count: response.data.length
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch live conversations', {
        service: 'LiveChatService',
        action: 'get_conversations_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch conversations')
    }
  }

  /**
   * Get detailed information about a specific conversation
   */
  async getConversationDetail(conversationId: string): Promise<LiveConversation> {
    try {
      logger.debug('Fetching conversation detail', {
        service: 'LiveChatService',
        action: 'get_conversation_detail',
        conversationId
      })

      const response = await api.get(`/live-chat/conversations/${conversationId}`)
      
      logger.info('Conversation detail fetched successfully', {
        service: 'LiveChatService',
        action: 'conversation_detail_fetched',
        conversationId
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch conversation detail', {
        service: 'LiveChatService',
        action: 'get_conversation_detail_error',
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch conversation detail')
    }
  }

  // ====================================
  // DASHBOARD METRICS
  // ====================================

  /**
   * Get comprehensive dashboard metrics
   */
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    try {
      logger.debug('Fetching dashboard metrics', {
        service: 'LiveChatService',
        action: 'get_dashboard_metrics'
      })

      const response = await api.get('/live-chat/dashboard/metrics')
      
      logger.info('Dashboard metrics fetched successfully', {
        service: 'LiveChatService',
        action: 'dashboard_metrics_fetched',
        metrics: {
          active_conversations: response.data.active_conversations,
          agents_online: response.data.agents_online
        }
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch dashboard metrics', {
        service: 'LiveChatService',
        action: 'get_dashboard_metrics_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch dashboard metrics')
    }
  }

  /**
   * Get quick status summary for real-time updates
   */
  async getLiveStatusSummary(): Promise<LiveStatusSummary> {
    try {
      logger.debug('Fetching live status summary', {
        service: 'LiveChatService',
        action: 'get_live_status_summary'
      })

      const response = await api.get('/live-chat/status/summary')
      
      logger.debug('Live status summary fetched', {
        service: 'LiveChatService',
        action: 'live_status_fetched',
        status: response.data
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch live status summary', {
        service: 'LiveChatService',
        action: 'get_live_status_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch live status')
    }
  }

  // ====================================
  // AGENT MANAGEMENT
  // ====================================

  /**
   * Get all agents for the current user's organization
   */
  async getAgents(): Promise<Agent[]> {
    try {
      logger.debug('Fetching agents', {
        service: 'LiveChatService',
        action: 'get_agents'
      })

      const response = await api.get('/live-chat/agents')
      
      logger.info('Agents fetched successfully', {
        service: 'LiveChatService',
        action: 'agents_fetched',
        count: response.data.length
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch agents', {
        service: 'LiveChatService',
        action: 'get_agents_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch agents')
    }
  }

  // ====================================
  // HANDOFF MANAGEMENT
  // ====================================

  /**
   * Get recent handoffs with filtering and pagination
   */
  async getHandoffs(
    status?: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<ConversationHandoff[]> {
    try {
      logger.debug('Fetching handoffs', {
        service: 'LiveChatService',
        action: 'get_handoffs',
        status,
        limit,
        offset
      })

      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      })

      if (status) params.append('status', status)

      const response = await api.get(`/live-chat/handoffs?${params}`)
      
      logger.info('Handoffs fetched successfully', {
        service: 'LiveChatService',
        action: 'handoffs_fetched',
        count: response.data.length
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch handoffs', {
        service: 'LiveChatService',
        action: 'get_handoffs_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch handoffs')
    }
  }

  // ====================================
  // POLLING FOR REAL-TIME UPDATES
  // ====================================

  /**
   * Start polling for live status updates
   * Returns a cleanup function to stop polling
   */
  startLiveStatusPolling(
    callback: (status: LiveStatusSummary) => void,
    intervalMs: number = 5000
  ): () => void {
    logger.info('Starting live status polling', {
      service: 'LiveChatService',
      action: 'start_polling',
      intervalMs
    })

    const intervalId = setInterval(async () => {
      try {
        const status = await this.getLiveStatusSummary()
        callback(status)
      } catch (error) {
        logger.warn('Live status polling error', {
          service: 'LiveChatService',
          action: 'polling_error',
          error: error instanceof Error ? error.message : 'Unknown error'
        })
      }
    }, intervalMs)

    // Return cleanup function
    return () => {
      logger.info('Stopping live status polling', {
        service: 'LiveChatService',
        action: 'stop_polling'
      })
      clearInterval(intervalId)
    }
  }

  /**
   * Start polling for conversation updates
   * Returns a cleanup function to stop polling
   */
  startConversationPolling(
    callback: (conversations: LiveConversation[]) => void,
    filters: ConversationFilters = {},
    intervalMs: number = 10000
  ): () => void {
    logger.info('Starting conversation polling', {
      service: 'LiveChatService',
      action: 'start_conversation_polling',
      intervalMs,
      filters
    })

    const intervalId = setInterval(async () => {
      try {
        const conversations = await this.getLiveConversations(filters)
        callback(conversations)
      } catch (error) {
        logger.warn('Conversation polling error', {
          service: 'LiveChatService',
          action: 'conversation_polling_error',
          error: error instanceof Error ? error.message : 'Unknown error'
        })
      }
    }, intervalMs)

    // Return cleanup function
    return () => {
      logger.info('Stopping conversation polling', {
        service: 'LiveChatService',
        action: 'stop_conversation_polling'
      })
      clearInterval(intervalId)
    }
  }

  // ====================================
  // AGENT MESSAGE SENDING
  // ====================================
  
  /**
   * Send message as agent to conversation
   */
  async sendAgentMessage(conversationId: string, message: string, agentId?: string): Promise<void> {
    try {
      logger.info('Agent sending message', {
        service: 'LiveChatService',
        action: 'send_agent_message',
        conversationId,
        messageLength: message.length,
        agentId
      })

      // First try the full live-chat endpoint with agent features
      let response;
      try {
        response = await api.post(`/live-chat/conversations/${conversationId}/agent-reply`, {
          conversation_id: conversationId,
          message: message,
          agent_id: agentId
        })
        logger.info('Agent message sent via live-chat endpoint', { conversationId, response: response.data })
        return // Exit if successful
      } catch (primaryError: any) {
        logger.warn('Live-chat endpoint failed, trying conversation history fallback', {
          conversationId,
          primaryError: primaryError.response?.data?.detail || primaryError.message
        })

        // Fallback to simpler conversation history endpoint
        response = await api.post(`/conversations/${conversationId}/agent-message`, {
          message: message
        })
        logger.info('Agent message sent via conversation history fallback', { conversationId, response: response.data })
      }
      
    } catch (error) {
      logger.error('Failed to send agent message (both endpoints failed)', {
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw error
    }
  }

  // ====================================
  // CONVERSATION CONTROL
  // ====================================
  
  /**
   * Transfer conversation from agent back to AI
   */
  async transferToAi(conversationId: string): Promise<void> {
    try {
      logger.info('Transferring conversation to AI', {
        service: 'LiveChatService',
        action: 'transfer_to_ai',
        conversationId
      })

      const response = await api.post(`/live-chat/conversations/${conversationId}/transfer-to-ai`)
      
      logger.info('Conversation transferred to AI successfully', {
        conversationId,
        response: response.data
      })
      
    } catch (error) {
      logger.error('Failed to transfer conversation to AI', {
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw error
    }
  }

  /**
   * Resolve conversation (mark as completed)
   */
  async resolveConversation(conversationId: string, notes?: string): Promise<void> {
    try {
      logger.info('Resolving conversation', {
        service: 'LiveChatService',
        action: 'resolve_conversation',
        conversationId,
        hasNotes: !!notes
      })

      const response = await api.post(`/conversations/${conversationId}/resolve`, {
        resolution_type: "human_resolved",
        notes
      })
      
      logger.info('Conversation resolved successfully', {
        conversationId,
        response: response.data
      })
      
    } catch (error) {
      logger.error('Failed to resolve conversation', {
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw error
    }
  }

  /**
   * End conversation (for agent interface)
   */
  async endConversation(conversationId: string): Promise<void> {
    try {
      logger.info('Ending conversation', {
        service: 'LiveChatService',
        action: 'end_conversation',
        conversationId
      })

      // For now, resolving is the same as ending
      await this.resolveConversation(conversationId, 'Conversation ended by agent')
      
      logger.info('Conversation ended successfully', { conversationId })
      
    } catch (error) {
      logger.error('Failed to end conversation', {
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw error
    }
  }
}

// Create and export service instance
export const liveChatService = new LiveChatService()

// Export service class for testing
export { LiveChatService }